import React, { useState } from "react";
import { Row, Col, Input, Table, Tabs, Tag, Button } from "antd";
import { ReloadOutlined, ClockCircleOutlined } from "@ant-design/icons";
import SearchIcon from "../../assets/images/searchIcon.svg";
import Swiggy from "../../assets/images/swiggy1.svg";
import Zomato from "../../assets/images/zomato.svg";
import SwiggyBolt from "../../assets/images/swiggyBolt.svg";
import Magicpin from "../../assets/images/magicpin-icon.png";
import "./OrderList.css";

const { TabPane } = Tabs;

const OrderList = ({ orders, selectedChannel, setSelectedChannel, searchValue, setSearchValue, loading, onOrderClick, onRefresh, statusDisplayMapping }) => {
  const [activeTab, setActiveTab] = useState("all");

  const channelIcons = { swiggy: Swiggy, zomato: Zomato, magicpin: Magicpin, swiggybolt: SwiggyBolt };
  const statusConfig = {
    NEW: { label: "New", color: "#4F23F1" },
    AK: { label: "Acknowledged", color: "#E99013" },
    FR: { label: "Food Ready", color: "#008000" },
    DI: { label: "Dispatched", color: "#4F23F1" },
    CO: { label: "Completed", color: "#07f307" },
    CC: { label: "Cancelled", color: "#dc2626" },
  };

  // Get tillData from localStorage
  const tillData = JSON.parse(localStorage.getItem("tillData"));

  // Filter orders by selected channel with case-insensitive comparison
  const filteredOrders = selectedChannel === "All Channels" ? orders : orders.filter((order) => order.channel?.toLowerCase() === selectedChannel.toLowerCase());

  const newOrders = filteredOrders.filter((order) => order.status === "NEW");
  const preparingOrders = filteredOrders.filter((order) => order.status === "AK");
  const awaitingPickupOrders = filteredOrders.filter((order) => order.status === "FR");
  const dispatchedOrders = filteredOrders.filter((order) => order.status === "DI");
  const completedOrders = filteredOrders.filter((order) => order.status === "CO");

  const getUniqueChannels = () => [...new Set(orders.map((order) => order.channel))].filter((channel) => channel && channel !== "Unknown");

  const formatCurrency = (amount) => `₹${amount.toFixed(2)}`;
  const formatTime = (timeString) => new Date(timeString).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });

  // Calculate time left (30 minutes from placed time) and determine color
  const calculateTimeLeft = (placedAt) => {
    const placedTime = new Date(placedAt);
    const endTime = new Date(placedTime.getTime() + 30 * 60 * 1000); // 30 minutes from placed time
    const now = new Date();
    const diffMs = endTime - now;
    let color = "#008000"; // Green for > 15 minutes
    if (diffMs <= 0) {
      return { text: "0:00", color };
    }
    const minutes = Math.floor(diffMs / 60000);
    const seconds = Math.floor((diffMs % 60000) / 1000);
    if (minutes < 5) {
      color = "#dc2626"; // Red for < 5 minutes
    } else if (minutes <= 15) {
      color = "#E99013"; // Yellow for 5-15 minutes
    }
    return { text: `${minutes}:${seconds.toString().padStart(2, "0")}`, color };
  };

  // Define table columns
  const columns = [
    {
      title: "Order",
      dataIndex: "orderNo",
      key: "orderNo",
      sorter: (a, b) => a.orderNo.localeCompare(b.orderNo),
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <img src={channelIcons[record.channel?.toLowerCase()] || Zomato} style={{ height: "20px" }} alt={record.channel || "Unknown"} />
          <span style={{ fontSize: "12px", fontWeight: 600 }}>{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Input
          placeholder="Search Order ID"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
      ),
      onFilter: (value, record) => record.orderNo.toLowerCase().includes(value.toLowerCase()),
      align: "left",
    },
    {
      title: "Platform ID",
      dataIndex: "externalId",
      key: "externalId",
      sorter: (a, b) => a?.externalId?.localeCompare(b.externalId),
      render: (text, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontSize: "12px", fontWeight: 600 }}>{text}</span>
        </div>
      ),
      filterDropdown: ({ setSelectedKeys, selectedKeys, confirm }) => (
        <Input
          placeholder="Search Platform ID"
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
      ),
      onFilter: (value, record) => record?.externalId?.toLowerCase().includes(value.toLowerCase()),
      align: "center",
    },
    {
      title: "Location",
      dataIndex: "location",
      key: "location",
      align: "center",
    },
    {
      title: "Placed At",
      dataIndex: "placedAt",
      key: "placedAt",
      sorter: (a, b) => new Date(a.placedAt) - new Date(b.placedAt),
      render: (text) => formatTime(text),
      align: "center",
    },
    {
      title: "Time Left",
      dataIndex: "timeLeft",
      key: "timeLeft",
      render: (text, record) => {
        const { text: timeText, color } = calculateTimeLeft(record.placedAt);
        return (
          <div style={{ display: "flex", alignItems: "center", gap: "4px", justifyContent: "center" }}>
            <ClockCircleOutlined style={{ fontSize: "12px", color }} />
            <span style={{ color }}>{timeText}</span>
          </div>
        );
      },
      align: "center",
    },
    // {
    //   title: "Pickup In",
    //   dataIndex: "pickupIn",
    //   key: "pickupIn",
    //   align: "center",
    // },
    {
      title: "Delivery Type",
      dataIndex: "deliveryType",
      key: "deliveryType",
      align: "center",
    },
    {
      title: "Rider Status",
      dataIndex: "riderStatus",
      key: "riderStatus",
      align: "center",
    },
    {
      title: "Total",
      dataIndex: "amount",
      key: "amount",
      render: (text) => formatCurrency(text),
      align: "center",
    },
    {
      title: "Order Status",
      dataIndex: "status",
      key: "status",
      render: (text) => (
        <Tag
          style={{
            backgroundColor: `${statusConfig[text]?.color}20`,
            color: statusConfig[text]?.color,
            border: `1px solid ${statusConfig[text]?.color}40`,
            fontSize: "10px",
            padding: "2px 6px",
            fontWeight: "bold",
            borderRadius: "5px",
          }}
        >
          {statusDisplayMapping[text] || "Unknown"}
        </Tag>
      ),
      align: "center",
    },
  ];

  // Prepare table data based on active tab and search
  const getTableData = () => {
    let dataSource = [];
    switch (activeTab) {
      case "all":
        dataSource = filteredOrders;
        break;
      case "inProgress":
        dataSource = preparingOrders;
        break;
      case "awaitingPickup":
        dataSource = awaitingPickupOrders;
        break;
      case "dispatched":
        dataSource = dispatchedOrders;
        break;
      case "completed":
        dataSource = completedOrders;
        break;
      default:
        dataSource = filteredOrders;
    }
    return dataSource
      .filter((order) => order.orderNo.toLowerCase().includes(searchValue.toLowerCase()) || order?.externalId?.toLowerCase().includes(searchValue.toLowerCase()))
      .map((order) => ({
        key: order.sOrderID,
        channel: order?.channel,
        externalId2: order?.externalId2 || "N/A",
        externalId: order?.externalId || "N/A",
        orderNo: order.orderNo,
        location: tillData?.tillAccess?.csBunit?.name || "N/A",
        placedAt: order.orderTime,
        timeLeft: calculateTimeLeft(order.orderTime).text,
        // pickupIn: order.pickupIn || "",
        deliveryType: order.deliveryType || "Partner",
        riderStatus: order?.riderStatus || "Unassigned",
        amount: order.amount,
        status: order.status,
      }));
  };
  const rowHeight = 40; // approximate row height in px (depends on size="small" or "middle")
  const headerHeight = 40;
  const maxTableHeight = window.innerHeight * 0.57; // 60% of viewport
  const scrollY = getTableData().length * rowHeight + headerHeight > maxTableHeight ? maxTableHeight : undefined; // no scroll if content fits

  return (
    <div className="order-list-wrapper" style={{ backgroundColor: "#fff", borderRadius: "8px", boxShadow: "0 1px 3px rgba(0,0,0,0.1)", height: "100%" }}>
      <div style={{ padding: "8px 12px" }}>
        <Row justify="space-between" align="middle" gutter={[8, 8]}>
          <Col>
            <span style={{ fontSize: "14px", fontWeight: 600 }}>Live Orders</span>
          </Col>
          <Col>
            <Row gutter={[4, 4]} align="middle">
              <Col>
                <Button
                  style={{
                    fontSize: "12px",
                    height: "28px",
                    padding: "0 8px",
                    backgroundColor: selectedChannel === "All Channels" ? "#2F3856" : "#fff",
                    color: selectedChannel === "All Channels" ? "#fff" : "#6b7280",
                    border: "1px solid #d1d5db",
                    borderRadius: "6px",
                    cursor: "pointer",
                  }}
                  onClick={() => setSelectedChannel("All Channels")}
                >
                  All Channels
                </Button>
              </Col>
              {getUniqueChannels().map((channel) => (
                <Col key={channel}>
                  <Button
                    style={{
                      fontSize: "12px",
                      height: "28px",
                      padding: "0 8px",
                      backgroundColor: selectedChannel === channel ? "#2F3856" : "#fff",
                      color: selectedChannel === channel ? "#fff" : "#6b7280",
                      border: "1px solid #d1d5db",
                      borderRadius: "6px",
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      cursor: "pointer",
                    }}
                    onClick={() => setSelectedChannel(channel)}
                  >
                    <img src={channelIcons[channel.toLowerCase()]} style={{ height: "16px" }} alt={channel} />
                    {channel}
                  </Button>
                </Col>
              ))}
            </Row>
          </Col>
        </Row>
        <Row style={{ marginTop: 8 }} align="middle" gutter={[8, 8]}>
          <Col flex="auto">
            <Input
              placeholder="Search by Order No/Platform ID..."
              prefix={<img src={SearchIcon} style={{ height: 16 }} alt="Search" />}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ height: 32, fontSize: 12, borderRadius: 6 }}
            />
          </Col>
          <Col flex="32px">
            <div
              onClick={onRefresh}
              style={{
                width: 32,
                height: 32,
                borderRadius: 6,
                background: "#2F3856",
                color: "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <ReloadOutlined style={{ fontSize: 16 }} title="Refresh Orders" />
            </div>
          </Col>
        </Row>
      </div>
      <div style={{ padding: "0 12px" }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab={`All (${filteredOrders.length})`} key="all" />
          <TabPane tab={`In Progress (${preparingOrders.length})`} key="inProgress" />
          <TabPane tab={`Awaiting Pickup (${awaitingPickupOrders.length})`} key="awaitingPickup" />
          <TabPane tab={`Dispatched (${dispatchedOrders.length})`} key="dispatched" />
          <TabPane tab={`Completed (${completedOrders.length})`} key="completed" />
        </Tabs>
        <Table
          columns={columns}
          className="aggregator"
          dataSource={getTableData()}
          pagination={false}
          loading={loading}
          sticky={true}
          onRow={(record) => ({
            onClick: () => onOrderClick(orders.find((order) => order.sOrderID === record.key)),
          })}
          rowClassName="clickable-row"
          scroll={{ x: "100%", y: "calc(100vh - 300px)" }} // Enable horizontal scrolling for responsiveness
          size="small"
        />
      </div>
    </div>
  );
};

export default OrderList;
